/**
 * UsageByAgentTable Component
 *
 * Displays token usage breakdown by agent as a sortable table.
 * Shows: Agent Name, Model, Total Tokens, Total Cost, Runs, Avg/Run.
 *
 * Related Ticket: AC-631
 * Parent Component: UsageDashboard
 *
 * Technical Design Reference: docs/technical/TD-AC-622-hierarchical-cost-breakdown.md (Section 4.5)
 */

import React, { useState } from 'react';
import { ArrowUpDown, Trash2 } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import type { TokenUsageByAgent } from '../../types/tokenUsage';

export interface UsageByAgentTableProps {
  /** Agent usage data */
  data: TokenUsageByAgent[] | null;
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Callback when an agent is clicked for detail view */
  onAgentClick?: (agent: TokenUsageByAgent) => void;
  /** Optional CSS class name */
  className?: string;
  /** Optional filter context to show in title (e.g., project or workflow name) */
  filterContext?: string;
}

type SortKey = 'agent_name' | 'model' | 'total_tokens' | 'total_cost' | 'run_count' | 'avg_cost_per_run';

const UsageByAgentTable: React.FC<UsageByAgentTableProps> = ({
  data,
  loading,
  error,
  onAgentClick,
  className = '',
  filterContext,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('total_cost');
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div data-testid="usage-by-agent-loading" className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
          <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div data-testid="usage-by-agent-error" className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}>
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div data-testid="usage-by-agent-empty" className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">
          Usage by Agent{filterContext && <span className="text-primary-600"> in {filterContext}</span>}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          {filterContext
            ? `No agent-attributed costs in ${filterContext} for this period`
            : 'No agent-attributed costs for this period'}
        </p>
      </div>
    );
  }

  // Sort data
  const sortedData = [...data].sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    // Handle null model values - treat null as empty string for sorting
    if (aVal === null) aVal = '';
    if (bVal === null) bVal = '';
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortAsc ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const formatCost = (value: number) => `$${value.toFixed(4)}`;
  const formatTokens = (value: number) => value.toLocaleString();

  const SortHeader = ({ label, sortKeyVal }: { label: string; sortKeyVal: SortKey }) => (
    <th
      role="columnheader"
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
      onClick={() => handleSort(sortKeyVal)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="w-3 h-3" />
      </div>
    </th>
  );

  return (
    <div data-testid="usage-by-agent-table" className={`bg-white dark:bg-cap-dark rounded-lg shadow ${className}`}>
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
          Usage by Agent{filterContext && <span className="text-primary-600"> in {filterContext}</span>}
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <SortHeader label="Agent" sortKeyVal="agent_name" />
              <SortHeader label="Model" sortKeyVal="model" />
              <SortHeader label="Total Tokens" sortKeyVal="total_tokens" />
              <SortHeader label="Total Cost" sortKeyVal="total_cost" />
              <SortHeader label="Runs" sortKeyVal="run_count" />
              <SortHeader label="Avg Cost/Run" sortKeyVal="avg_cost_per_run" />
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((agent) => (
              <tr
                key={agent.agent_id ?? 'unattributed'}
                className={`${onAgentClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900' : ''}`}
                onClick={() => onAgentClick?.(agent)}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className={agent.is_deleted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-warm-white'}>
                      {agent.agent_name}
                    </span>
                    {agent.is_deleted && (
                      <Tooltip content="Deleted agent">
                      <span>
                        <Trash2 className="w-3 h-3 ml-2 text-gray-400" />
                      </span>
                      </Tooltip>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {agent.model || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-warm-white">
                  {formatTokens(agent.total_tokens)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-warm-white">
                  {formatCost(agent.total_cost)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {agent.run_count}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatCost(agent.avg_cost_per_run)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsageByAgentTable;
